import { Component, EventEmitter, Output, OnInit, OnDestroy, HostListener } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageType, RcsMessage } from 'src/app/_models/messaging/messages/rcs-messages/rcs-message.model';
import { RcsService } from 'src/app/_services/messaging/messages/rcs-messages/rcs.service';
import { RcsMessageService } from 'src/app/_services/messaging/messages/rcs-messages/rcs-message.service';
import { ValidationService } from 'src/app/_services/messaging/messages/rcs-messages/validation.service';
import { ExecutionResultDto } from 'src/app/_models/execution-result-model';
import { ExecutionResult } from 'src/app/_models/execution-result-enum';
import { FolderDto } from 'src/app/_models/messaging/messages/folder-dto';
import { SelectListOption } from 'src/app/_models/system/select-list-option';
import { Observable, Subscription } from 'rxjs';
import { UnsubscribeService } from 'src/app/_services/system/unsubscribe.service';
import { NotificationService } from 'src/app/_services/notification.service';
import { FolderDtoAdapter } from 'src/app/_models/messaging/messages/folder-dto.adapter';
import { MessageFolderService } from 'src/app/_services/messaging/messages/message-folder.service';
import { ComponentCanDeactivate } from 'src/app/_guards/pending-changes-guard';
import { MessageTypeEnum } from 'src/app/_models/messaging/messages/message-type-enum';
import { HelperService } from 'src/app/_services/system/helpers/helper.service';
import { LocalStorageService } from 'src/app/_services/system/localStorage/localStorage.service';

@Component({
  selector: 'app-mobile-type-selector',
  templateUrl: './mobile-type-selector.component.html',
  styleUrls: ['./mobile-type-selector.component.scss']
})
export class MobileTypeSelectorComponent implements OnInit, OnDestroy {
  currentMessage: RcsMessage | null = null;
  selectedType: 'sms' | 'rcs' | null = null;
  selectedRcsType: MessageType = null;
  MessageType = MessageType;
  
  isEditMode = false;
  messageId: number;
  currentEditMessage: any = null;
  isInternalUser = false;

  _formGroupCreateMessage: UntypedFormGroup;
  _messageFolderSelectListOptions: SelectListOption[] = [];
  _selectedMessageFolderId = 0;
  _showSpinner = false;
  _subscriptionMessageFolders: Subscription;
  _hasClientAnyRcsApp: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private rcsService: RcsService,
    private rcsMessageService: RcsMessageService,
    private validationService: ValidationService,
    private messageFolderService: MessageFolderService,
    private messageFolderDtoAdapter: FolderDtoAdapter,
    private notificationService: NotificationService,
    private helperService: HelperService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.isInternalUser = this.helperService.isLoggedInUserMDUser();
    // Check if this is edit mode
    this.messageId = +this.route.snapshot.paramMap.get('messageId');
    this.isEditMode = !!this.messageId;
    
    this.rcsService.getCurrentMessage().subscribe(message => {
      this.currentMessage = message;
    });

    this._formGroupCreateMessage = new UntypedFormGroup({
      messageName: new UntypedFormControl('', Validators.required),
      messageFolder: new UntypedFormControl({ value: 0, disabled: true }, Validators.required)
    });

    this.helperService.hasClientAnyRcsApplication(Number(this.localStorageService.getCurrentClientId)).subscribe(result => {
      this._hasClientAnyRcsApp = result;
    });

    // Load message folders first, then load message data for edit mode
    this.populateMessageFoldersDropdown();
    this.subscribeToFormControls();
  }

  loadMessageForEdit(): void {
    if (this.messageId) {
      this.rcsMessageService.get(this.messageId).subscribe(
        (result) => {
          if (result.executionResult === ExecutionResult.success) {
            const message = result.data;
            
            // Store message data for child components
            this.currentEditMessage = message;
            
            // Set form values using correct property names from server response
            this._formGroupCreateMessage.patchValue({
              messageName: message.MessageName || '',
              messageFolder: message.FolderID || 0
            });
            
            // Update selected folder ID
            this._selectedMessageFolderId = message.FolderID || 0;
            
            // Set message type selections
            this.selectedType = 'rcs';
            
            // Map rcsType to MessageType enum
            switch (message.RCSType?.toLowerCase()) {
              case 'text':
                this.selectedRcsType = MessageType.TEXT;
                break;
              case 'richcard':
                this.selectedRcsType = MessageType.STANDALONE;
                break;
              case 'carousel':
                this.selectedRcsType = MessageType.CAROUSEL;
                break;
              default:
                this.selectedRcsType = MessageType.TEXT;
            }
            
          } else {
            this.notificationService.showError('Failed to load message: ' + result.message);
          }
        },
        (error) => {
          this.notificationService.showError('Error loading message');
          console.error('Error loading message:', error);
        }
      );
    }
  }

  ngOnDestroy() {
    UnsubscribeService.unsubscribe(this._subscriptionMessageFolders);
  }

  onMessageChange(message: RcsMessage): void {
    this.rcsService.updateMessage(message);
  }

  selectType(type: 'sms' | 'rcs') {
    this.selectedType = type;

    if (type === 'sms') {
      this.selectedRcsType = null;
      }
  }

  selectRcsType(type: MessageType) {
    this.selectedRcsType = type;
  }

  navigateToMessagesPage() {
    console.log('Navigating to messages page');
    this.router.navigate(['messages']);
  }

  navigateToSendPage() {
    if (!this.messageId) {
      console.error('No message ID available for navigation');
      return;
    }

    let messageTypeEnum: MessageTypeEnum;

    if (this.selectedType === 'sms') {
      messageTypeEnum = MessageTypeEnum.sms;
    } else if (this.selectedType === 'rcs') {
      // Determine the specific RCS type based on selectedRcsType
      switch (this.selectedRcsType) {
        case MessageType.TEXT:
          messageTypeEnum = MessageTypeEnum['rcs-text'];
          break;
        case MessageType.STANDALONE:
          messageTypeEnum = MessageTypeEnum['rcs-rich-card'];
          break;
        case MessageType.CAROUSEL:
          messageTypeEnum = MessageTypeEnum['rcs-carousel'];
          break;       
      }
    } else {
      console.error('Unknown message type');
      return;
    }

    this.router.navigate(['messages/message-send', this.messageId, messageTypeEnum]);
  }

  populateMessageFoldersDropdown() {
    this.messageFolderService.getAll().subscribe((executionResultDto: ExecutionResultDto) => {
      this.showSpinner();
      this._formGroupCreateMessage.get('messageFolder').disable();

      if (executionResultDto.executionResult === ExecutionResult.success) {
        const messageFolderDtos = executionResultDto.data.map((messageFolderDto: FolderDto) =>
          this.messageFolderDtoAdapter.adapt(messageFolderDto));

        this._messageFolderSelectListOptions = messageFolderDtos
          .filter((messageFolderDto: FolderDto) => messageFolderDto.name && messageFolderDto.name.trim() !== '')
          .map((messageFolderDto: FolderDto) =>
            new SelectListOption(messageFolderDto.id, messageFolderDto.name));
      } else {
        this.notificationService.showError(executionResultDto.message);
      }

      this._formGroupCreateMessage.get('messageFolder').enable();
      this.hideSpinner();
      
      // Load message data after folders are loaded (for edit mode)
      if (this.isEditMode) {
        this.loadMessageForEdit();
      }
    }, error => {
      this.hideSpinner();
      if (this.isEditMode) {
        this.loadMessageForEdit();
      }
    });
  }

  subscribeToFormControls() {
    this._subscriptionMessageFolders = this._formGroupCreateMessage.get('messageFolder').valueChanges.subscribe((folderId: number) => {
      this._selectedMessageFolderId = folderId;
    });
  }

  showSpinner() {
    this._showSpinner = true;
  }

  hideSpinner() {
    this._showSpinner = false;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return true; 
  }
}